import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { AFFILIATE_PROVIDERS, DEFAULT_PROVIDER_KEY } from '../constants/affiliateProviders';
import { API_BASE } from '../lib/apiBase';

/**
 * Provides the merged (built-in + DB-custom) affiliate-provider map to the
 * whole app. Custom providers are persisted in MongoDB and fetched once on
 * boot. Built-ins always win on key collisions so admin-created entries
 * can never accidentally shadow GetYourGuide / Viator / Tiqets / GoCity.
 *
 * Schema (matches `getProvider(...)` consumers in ExperienceCard):
 *   { name, bg, hoverBg, logo, logoClass, logoHeight, custom? }
 *
 * Built-ins use `logoClass` (Tailwind utilities) for legacy styling.
 * Custom entries instead use a numeric `logoHeight` — `getProvider`
 * returns BOTH so the renderer can pick whichever it prefers.
 */
const AffiliateProvidersContext = createContext(null);

const buildLogoClass = (height) => {
  // Default tailwind h-4 ≈ 16px. We keep h-4 for the standard case so the
  // built-in look is preserved, and fall back to inline style for non-16px.
  if (!height || height === 16) return 'h-4';
  return ''; // empty class → inline `style={{ height: `${height}px` }}` wins
};

export function AffiliateProvidersProvider({ children }) {
  const [custom, setCustom] = useState([]);

  const fetchCustom = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/affiliate-providers`);
      if (!res.ok) return;
      const data = await res.json();
      setCustom(data.providers || []);
    } catch (_err) {
      // Network blip — silently render built-ins only.
    }
  }, []);

  useEffect(() => {
    fetchCustom();
  }, [fetchCustom]);

  const merged = useMemo(() => {
    const out = { ...AFFILIATE_PROVIDERS };
    for (const p of custom) {
      if (out[p.key]) continue; // built-ins win on collision
      out[p.key] = {
        name: p.name,
        bg: p.bg,
        hoverBg: p.hoverBg,
        logo: p.logo || '',
        logoHeight: p.logoHeight || 16,
        logoClass: buildLogoClass(p.logoHeight || 16),
        custom: true,
      };
    }
    return out;
  }, [custom]);

  const getProvider = useCallback(
    (key) => merged[key] || merged[DEFAULT_PROVIDER_KEY],
    [merged],
  );

  const value = useMemo(
    () => ({ providers: merged, getProvider, refresh: fetchCustom }),
    [merged, getProvider, fetchCustom],
  );

  return (
    <AffiliateProvidersContext.Provider value={value}>
      {children}
    </AffiliateProvidersContext.Provider>
  );
}

export function useAffiliateProviders() {
  const ctx = useContext(AffiliateProvidersContext);
  if (!ctx) {
    // Fallback so components used outside the provider tree (tests, isolated
    // previews) still work — only built-ins are visible.
    return {
      providers: AFFILIATE_PROVIDERS,
      getProvider: (key) =>
        AFFILIATE_PROVIDERS[key] || AFFILIATE_PROVIDERS[DEFAULT_PROVIDER_KEY],
      refresh: () => {},
    };
  }
  return ctx;
}
